import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { RocFormData } from '../../../../types/RocFormData'
import { RocFormField } from '../../../../types/RocFormField'
import { RocAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Sensory subsection adapter
 * Handles sensory status for all five senses plus system summary (6 fields)
 */
export class SensoryAdapter extends BaseSubsectionAdapter<RocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // All sensory status fields
    const statusEyes = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SENSORY_STATUS_EYES
    )?.at(0)

    const statusEars = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SENSORY_STATUS_EARS
    )?.at(0)

    const statusNose = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SENSORY_STATUS_NOSE
    )?.at(0)

    const statusTouch = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SENSORY_STATUS_TOUCH
    )?.at(0)

    const statusTaste = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SENSORY_STATUS_TASTE
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [RocFormField.SENSORY_STATUS_EYES]: statusEyes,
      [RocFormField.SENSORY_STATUS_EARS]: statusEars,
      [RocFormField.SENSORY_STATUS_NOSE]: statusNose,
      [RocFormField.SENSORY_STATUS_TOUCH]: statusTouch,
      [RocFormField.SENSORY_STATUS_TASTE]: statusTaste,
      [RocFormField.SENSORY_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RocFormField.SENSORY_STATUS_EYES)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SENSORY_STATUS_EYES,
        value: this.getFormDataField(RocFormField.SENSORY_STATUS_EYES)
      })
    }

    if (this.formDataFieldExists(RocFormField.SENSORY_STATUS_EARS)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SENSORY_STATUS_EARS,
        value: this.getFormDataField(RocFormField.SENSORY_STATUS_EARS)
      })
    }

    if (this.formDataFieldExists(RocFormField.SENSORY_STATUS_NOSE)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SENSORY_STATUS_NOSE,
        value: this.getFormDataField(RocFormField.SENSORY_STATUS_NOSE)
      })
    }

    if (this.formDataFieldExists(RocFormField.SENSORY_STATUS_TOUCH)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SENSORY_STATUS_TOUCH,
        value: this.getFormDataField(RocFormField.SENSORY_STATUS_TOUCH)
      })
    }

    if (this.formDataFieldExists(RocFormField.SENSORY_STATUS_TASTE)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SENSORY_STATUS_TASTE,
        value: this.getFormDataField(RocFormField.SENSORY_STATUS_TASTE)
      })
    }

    if (this.formDataFieldExists(RocFormField.SENSORY_SYSTEM_SUMMARY)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SYSTEM_SUMMARY,
        value: this.getFormDataField(RocFormField.SENSORY_SYSTEM_SUMMARY)
      })
    }

    return mutations
  }
}
